// @flow
import * as React from "react"
import { compose, lifecycle, withHandlers, withStateHandlers, type HOC } from "recompose"
import { ThemeProvider } from "styled-components"

import withUser from "hoc/withUser"
import withRouter from "hoc/withRouter"
import withConnect from "hoc/withConnect"
import withOnMount from "hoc/withOnMount"
import withOnNextProps from "hoc/withOnNextProps"
import { query } from "global/apolloClient/helpers"
import { type Theme, defaultTheme } from "global/theme"
import { loadGlobalStyles } from "global/global-styles"
import { selectLoggingOut } from "data/user/selectors"
import { setAppConfig, setAllProductGroups, setTheme } from "data/app/actions"
import { selectAppConfig } from "data/app/selectors"
import { appConfigQuery, themeQuery, productGroupListQuery } from "data/app/graphql/queries"
import ErrorPage from "pages/Error"
import GDPRWarning from "components/GDPRWarning"
import Loader from "components/Loader"
import Routes from "./Routes"
import MasterModal from "./MasterModal"
import Notifications from "./Notifications"

const Content = ({ loggingOut, error, theme, loading }) => {
  function renderInner() {
    if (error) {
      return <ErrorPage />
    } else if (loading || loggingOut) {
      return <Loader />
    } else {
      return (
        <React.Fragment>
          <GDPRWarning />
          <Routes />
          <Notifications />
          <MasterModal />
        </React.Fragment>
      )
    }
  }

  return (
    <ThemeProvider theme={theme}>
      {renderInner()}
    </ThemeProvider>
  )
}

const mapStateToProps = state => ({
  appConfig: selectAppConfig(state),
  loggingOut: selectLoggingOut(state),
})

const mapDispatchToProps = {
  setAppConfig,
  setAllProductGroups,
  /** NOTE: Not calling it `setTheme` to avoid naming conflict with the injected theme setter */
  setThemeInState: setTheme,
}

type Outter = {|
  error: ?string,
|}

const enhancer: HOC<*, Outter> = compose(
  withRouter,
  withConnect(mapStateToProps, mapDispatchToProps),
  withUser(),
  withStateHandlers(
    {
      loading: true,
      theme: defaultTheme,
    },
    {
      setLoading: () => v => ({ loading: v }),
      /** Sets the theme that gets injected by ThemeProvider */
      setTheme: () => (theme: Theme) => ({ theme }),
    },
  ),
  withHandlers({
    prefetchQueries: props => () => {
      props.setLoading(true)
      Promise.all([
        query(appConfigQuery),
        query(themeQuery),
        query(productGroupListQuery),
      ])
        .then(
          ([
            appConfig,
            backendTheme,
            productGroups,
          ]) => {
            console.log('Loaded app config:', appConfig)
            props.setAppConfig(appConfig)
            props.setAllProductGroups(productGroups)
            // Set the theme into the store, which contains header and footer
            props.setThemeInState(backendTheme)

            // Set the theme that gets injected by ThemeProvider, which includes
            // colours and border radiuses
            const injectedTheme = {
              ...defaultTheme,
              color: {
                ...defaultTheme.color,
                ...backendTheme.colors,
              },
              borderRadiuses: {
                ...defaultTheme.borderRadiuses,
                ...backendTheme.borderRadiuses,
              },
            }
            props.setTheme(injectedTheme)

            // Initialize the global styles, which includes fonts
            loadGlobalStyles(injectedTheme, {
              ...backendTheme.fonts,
            })
            
            props.setLoading(false)
          },
        )
        .catch(() => props.setLoading(false))
    },
  }),
  withOnMount(props => {
    if (!props.loggingOut) {
      props.prefetchQueries()
    }
  }),
  withOnNextProps((prev, next) => {
    if (prev.loggingOut && !next.loggingOut) {
      prev.prefetchQueries()
    }
  }),
  lifecycle({
    componentDidUpdate() {
      const { error, loggingOut, appConfig } = this.props
      if (error || loggingOut) {
        document.title = "Awayco"
      } else {
        document.title = appConfig ? appConfig.tenantTitle: "Awayco"
      }
    }
  })
)

export default enhancer(Content)



// WEBPACK FOOTER //
// ./src/AppComponent/Content/index.js